import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaSave, FaTimes } from 'react-icons/fa';
import Select from 'react-select';
import DropdownButton from 'react-bootstrap/DropdownButton';
import Dropdown from 'react-bootstrap/Dropdown';
import { toast } from 'react-hot-toast';
import { getCurrencySymbol, numberFormatter } from 'utils/currency';
import { useDropzone } from 'react-dropzone';
import moment from 'moment';

// ✅ Clear
const PaymentModalMobile = ({ showModal, details, handleClose, handleSaveAndClose }) => {
    const [disableStatus, setDisablestatus] = useState(false);
    const [hasAttachment, setHasAttachment] = useState(false);
    const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState('');
    const [uploadedFileSize, setUploadedFileSize] = useState('');
    const [uploadedPaymentAttachment, setUploadedPaymentAttachment] = useState(null);
    const [currentDate] = useState(moment().format('YYYY-MM-DD'));
    const [vendorCreditLists, setVendorCreditLists] = useState([]);
    const [selectedVendorCredit, setSelectedVendorCredit] = useState(null);

    const paymentTypes = [
        { label: 'Bank Transfer', value: 'bank_transfer' },
        { label: 'Bank Cheque', value: 'bank_cheque' },
        { label: 'Cash', value: 'cash' },
        { label: 'Credit Card', value: 'credit_card' },
        { label: 'Vendor Credit', value: 'vendor_credit' },
    ];

    const currencies = [
        { label: 'USD($)', value: 'usd', symbol: '$' },
        { label: 'EUR(€)', value: 'eur', symbol: '€' },
        { label: 'AED(AED)', value: 'aed', symbol: 'AED' },
    ];

    const [paymentDetails, setPaymentDetails] = useState({
        currency: details?.currency,
        paymentType: 'bank_transfer',
        paymentDate: currentDate,
        amount: '',
        note: '',
    });
    const [defaultCurrency, setDefaultCurrency] = useState('USD($)');
    const [remaningBalance, setRemaningBalance] = useState(0);

    useEffect(() => {
        setDefaultCurrency(currencies?.find((item) => item.value === paymentDetails?.currency)?.label || 'USD($)');
        setRemaningBalance((details?.converted_currency_open_balance?.[paymentDetails?.currency] ?? 0) - (parseFloat(paymentDetails?.amount) || 0));
    }, [paymentDetails?.currency, paymentDetails?.amount, details]);

    useEffect(() => {
        api.getSelectOptionsListWithId('get_open_vendor_credits_by_vendor', details?.supplier?.id).then((res) => {
            if (res?.status) {
                const tempList = res.data.map((item) => ({
                    ...item,
                    label: `${item.option_label_name}`,
                    value: item.id,
                }));
                setVendorCreditLists(tempList);
            }
        });
    }, [details?.supplier?.id]);

    const allowedExtensions = ['pdf', 'jpeg', 'jpg', 'png'];
    const validateFileExtension = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        return allowedExtensions.includes(extension);
    };

    const uploadAttachment = async (attachment) => {
        setUploadedFileName(attachment.name);
        setUploadedPaymentAttachment(attachment);
        let sizeStr = "";
        const sizeInKB = attachment.size / 1024;
        if (sizeInKB >= 1024) {
            const sizeInMB = sizeInKB / 1024;
            sizeStr = `${sizeInMB.toFixed(2)} MB`;
        } else {
            sizeStr = `${sizeInKB.toFixed(2)} KB`;
        }
        setUploadedFileSize(sizeStr);
        setHasAttachment(true);
        setIsAttachmentLoading(true);
        setTimeout(() => setIsAttachmentLoading(false), 1000);
    };

    const onPaymentDrop = useCallback((acceptedFiles) => {
        if (validateFileExtension(acceptedFiles[0].name)) {
            uploadAttachment(acceptedFiles[0]);
        } else {
            toast.error('Invalid file extension. Please select a PDF, JPEG, JPG, or PNG file.');
        }
    }, []);
    const { getRootProps: getPaymentRootProps, getInputProps: getPaymentInputProps } = useDropzone({ onDrop: onPaymentDrop });

    const handleRemoveAttachment = () => {
        setUploadedFileName('');
        setUploadedFileSize('');
        setHasAttachment(false);
        setUploadedPaymentAttachment(null);
    };

    const handleChangeCurrency = (currency) => {
        setDefaultCurrency(currency.label);
        setPaymentDetails((prev) => ({
            ...prev,
            currency: currency.value
        }));
        setRemaningBalance((details?.converted_currency_open_balance?.[currency.value] ?? 0) - (parseFloat(paymentDetails?.amount) || 0));
    };

    const handleCreditChange = (e) => {
        setSelectedVendorCredit(e);
        setPaymentDetails((prev) => ({
            ...prev,
            amount: e?.amount,
            currency: e?.currency
        }));
    };

    const handleSubmitForm = (e) => {
        e.preventDefault();
        if (!paymentDetails?.paymentType) {
            toast.error('Payment type is required');
            return;
        }
        if (!paymentDetails?.paymentDate) {
            toast.error('Payment date is required');
            return;
        }
        if (!paymentDetails?.amount || parseFloat(paymentDetails?.amount) <= 0) {
            toast.error('Payment amount is required');
            return;
        }
        if (paymentDetails?.paymentType === 'vendor_credit' && !selectedVendorCredit?.id) {
            toast.error('Please select a vendor credit');
            return;
        }
        const formData = new FormData();
        formData.append('purchase_invoice_id', details?.id);
        formData.append('currency', paymentDetails?.currency);
        formData.append('payment_type', paymentDetails?.paymentType);
        formData.append('payment_date', paymentDetails?.paymentDate);
        formData.append('amount', paymentDetails?.amount);
        formData.append('note', paymentDetails?.note);
        if (uploadedPaymentAttachment) formData.append('attachment', uploadedPaymentAttachment);
        if (selectedVendorCredit?.id) formData.append('vendor_credit_id', selectedVendorCredit?.id);

        toast.promise(
            api.addPurchaseOrderPayment(details?.id, formData).then((res) => {
                if (res.status) handleSaveAndClose();
                return res?.status ? res : null;
            }),
            {
                loading: 'Saving payment...',
                success: (data) => `${data.message}`,
                error: <b>Failed to save payment</b>
            }
        );
    };

    if (!showModal) return null;

    return (
        <div
            className="d-flex"
            style={{
                width: '100vw',
                height: '100dvh',
                position: 'fixed',
                left: 0,
                top: 0,
                zIndex: 20,
                background: 'rgba(0,0,0,0.07)',
                justifyContent: 'center',
                alignItems: 'end',
                paddingBottom: '90px',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)'
            }}
        >
            <div
                className="popup product-popup-mobile d-md-none"
                style={{
                    position: 'relative',
                    transform: 'none',
                    top: 0,
                    left: 0,
                    maxHeight: 'calc(100dvh - 110px)',
                    maxWidth: '90%',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: 'rgb(0 0 0 / 37%) 0px 0px 20px',
                    width: '100%',
                    padding: '24px 16px 0 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto'
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <h2 className="h4 mb-0 font-weight-bold">Register payment</h2>
                </div>
                <form onSubmit={handleSubmitForm}>
                    <div style={{ marginBottom: '20px' }}>
                        <div className="row" style={{ marginTop: '15px' }}>
                            <div className="col-12 text-center">
                                You are going to settle a payment for <span className='font500'>{details?.purchase_order_id}</span>
                                <br />with an amount of
                                <span className='text-primary font500'>
                                    {getCurrencySymbol(paymentDetails?.currency)}{' '}
                                    {parseFloat(details?.converted_currency_open_balance?.[paymentDetails?.currency] ?? 0)
                                        .toFixed(2)
                                        .replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                                </span>.
                            </div>
                        </div>
                    </div>
                    <div className="row m-0">
                        {/* Payment Type */}
                        <div className="form-group col-md-6">
                            <label htmlFor="paymentType">Payment Type</label>
                            <Select
                                menuPosition={'fixed'}
                                options={paymentTypes}
                                value={paymentTypes?.find((y) => y?.value === paymentDetails?.paymentType)}
                                className="rs-container"
                                classNamePrefix="rs"
                                placeholder="Select or type..."
                                onChange={(e) => {
                                    setPaymentDetails((prev) => ({
                                        ...prev,
                                        paymentType: e?.value
                                    }));
                                    if (e?.value !== 'vendor_credit') setSelectedVendorCredit(null);
                                }}
                            />
                        </div>
                        {/* Date */}
                        <div className="form-group col-md-6">
                            <label htmlFor="paymentAmount">Date</label>
                            <input
                                type="date"
                                className="form-control"
                                id="paymentDate"
                                value={paymentDetails?.paymentDate || ''}
                                onChange={(e) => {
                                    setPaymentDetails((prev) => ({
                                        ...prev,
                                        paymentDate: e.target.value
                                    }));
                                }}
                            />
                        </div>
                        {/* Vendor Credit */}
                        {paymentDetails?.paymentType === 'vendor_credit' && (
                            <div className="form-group col-12">
                                <label htmlFor="paymentAmount">Available Credits</label>
                                <Select
                                    className="rs-container"
                                    classNamePrefix="rs"
                                    options={vendorCreditLists}
                                    menuPlacement="auto"
                                    menuPosition="fixed"
                                    placeholder="Select or type..."
                                    onChange={handleCreditChange}
                                />
                            </div>
                        )}
                        {/* Amount */}
                        <div className="form-group col-md-6">
                            <label htmlFor="paymentAmount">Amount</label>
                            <div className="custom-input-group payment-input-group">
                                {paymentDetails?.paymentType === 'vendor_credit' ? (
                                    <div className="d-flex input-group-2-form input-group-2-form-currency">
                                        <input
                                            type="text"
                                            className="form-control left-input"
                                            value={selectedVendorCredit?.currency_label}
                                            disabled={true}
                                            style={{ width: '75px' }}
                                        />
                                        <input
                                            type="number"
                                            className="form-control right-input"
                                            value={paymentDetails?.amount}
                                            onChange={(e) => {
                                                setPaymentDetails((prev) => ({
                                                    ...prev,
                                                    amount: e.target.value
                                                }));
                                                setRemaningBalance((details?.converted_currency_open_balance?.[paymentDetails?.currency] ?? 0) - (parseFloat(e.target.value) || 0));
                                            }}
                                            autoComplete={'off'}
                                            autoFocus
                                            onClick={(e) => {
                                                if (e.target.value === '0' || e.target.value === '0.00') {
                                                    setPaymentDetails((prev) => ({ ...prev, amount: '' }));
                                                }
                                            }}
                                            onBlur={(e) => {
                                                if (e.target.value === '') {
                                                    setPaymentDetails((prev) => ({ ...prev, amount: '0.00' }));
                                                }
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center">
                                        <DropdownButton
                                            variant="outline-secondary"
                                            title={defaultCurrency}
                                            id="input-group-dropdown-1"
                                        >
                                            {currencies.map((currency) => (
                                                <Dropdown.Item
                                                    href="#"
                                                    key={currency.value}
                                                    onClick={() => handleChangeCurrency(currency)}
                                                >
                                                    {currency.label}
                                                </Dropdown.Item>
                                            ))}
                                        </DropdownButton>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={paymentDetails?.amount}
                                            onChange={(e) => {
                                                setPaymentDetails((prev) => ({
                                                    ...prev,
                                                    amount: e.target.value
                                                }));
                                                setRemaningBalance((details?.converted_currency_open_balance?.[paymentDetails?.currency] ?? 0) - (parseFloat(e.target.value) || 0));
                                            }}
                                            autoComplete={'off'}
                                            autoFocus
                                            onClick={(e) => {
                                                if (e.target.value === '0' || e.target.value === '0.00') {
                                                    setPaymentDetails((prev) => ({ ...prev, amount: '' }));
                                                }
                                            }}
                                            onBlur={(e) => {
                                                if (e.target.value === '') {
                                                    setPaymentDetails((prev) => ({ ...prev, amount: '0.00' }));
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Open */}
                        <div className="form-group col-md-6">
                            <label htmlFor="paymentAmount">Open ({getCurrencySymbol(paymentDetails?.currency)})</label>
                            <input
                                type="text"
                                className="form-control"
                                value={numberFormatter.format(remaningBalance)}
                                readOnly
                            />
                        </div>
                        {/* Note */}
                        <div className="form-group col-md-12">
                            <label htmlFor="paymentAmount">Note</label>
                            <textarea
                                className="form-control"
                                value={paymentDetails?.note}
                                onChange={(e) => setPaymentDetails((prev) => ({ ...prev, note: e.target.value }))}
                                rows={2}
                                placeholder="Add note"
                            />
                        </div>
                        {/* Attachment */}
                        <div className="form-group col-12">
                            <div style={{ marginTop: '20px' }}>
                                {hasAttachment ? (
                                    <div className="payment-modal-attachment-container">
                                        <div className="row">
                                            <div className="col-12" style={{ display: 'flex', alignItems: 'center' }}>
                                                <div className="payment-modal-attachment-name">
                                                    <div>{uploadedFileName}</div>
                                                    <div>{uploadedFileSize}</div>
                                                </div>
                                                <div
                                                    className="payment-modal-attachment-delete"
                                                    onClick={handleRemoveAttachment}
                                                    style={{ marginLeft: 10, cursor: 'pointer' }}
                                                >
                                                    <FaTimes size={16} />
                                                </div>
                                            </div>
                                            {isAttachmentLoading && (
                                                <div className="col-12">
                                                    <div className="animated-progress-bar"></div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div {...getPaymentRootProps()}>
                                        <div className="add-item-empty-div">
                                            <button type="button" className="btn btn-primary btn-outlined-main-primary dragText">
                                                Add File here
                                            </button>
                                        </div>
                                        <input {...getPaymentInputProps()} style={{ display: 'none' }} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Sticky bottom buttons */}
                    <div
                        className="btnarea"
                        style={{
                            position: 'sticky',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            background: '#fff',
                            padding: '16px 0 12px 0',
                            zIndex: 30,
                            display: 'flex',
                            gap: 10,
                            justifyContent: 'center',
                            marginTop: 'auto'
                        }}
                    >
                        <button
                            className="btn btn-primary btn-outlined-main"
                            type="submit"
                            disabled={disableStatus}
                            style={{ marginRight: '10px' }}
                        >
                            <FaSave /> Save
                        </button>
                        <button className="btn btn-main-danger btn-outlined-danger" type="button" onClick={handleClose}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PaymentModalMobile;
